import { Box, Flex, Text } from "@radix-ui/themes";

import { formatClockTime, formatCurrency } from "../overview.format";
import type { OverviewAccountChartControllerValue } from "./OverviewAccountChart.controller";

/** ~96px compact chart — `h-24` is exactly 96px. */
const chartBoxClassName = "h-24 w-full";

export type OverviewAccountChartPresentationProps =
  OverviewAccountChartControllerValue;

export const OverviewAccountChartPresentation = ({
  containerRef,
  hovered,
  isLoading,
  hasError,
  hasInsufficientData,
}: OverviewAccountChartPresentationProps) => {
  if (isLoading) {
    return (
      <Flex align="center" justify="center" className={chartBoxClassName}>
        <Text size="1" color="gray">
          Loading chart
        </Text>
      </Flex>
    );
  }

  if (hasError) {
    return (
      <Flex align="center" justify="center" className={chartBoxClassName}>
        <Text size="1" color="red">
          Chart unavailable
        </Text>
      </Flex>
    );
  }

  if (hasInsufficientData) {
    return (
      <Flex align="center" justify="center" className={chartBoxClassName}>
        <Text size="1" color="gray">
          No history for this period
        </Text>
      </Flex>
    );
  }

  return (
    <Box position="relative" className={chartBoxClassName}>
      <div
        ref={containerRef}
        data-testid="overview-account-chart-surface"
        className="h-full w-full"
      />
      {hovered ? (
        <Box
          data-testid="overview-account-chart-tooltip"
          position="absolute"
          top="0"
          px="2"
          py="1"
          // The offset tracks the pointer, so it cannot be a static class.
          style={{
            left: `${hovered.left}px`,
            width: `${hovered.tooltipWidth}px`,
          }}
          // `z-10` clears the chart canvases, which sit at z-index 1 and 2.
          className="atd-elevation-2 pointer-events-none z-10 rounded-[var(--radius-2)] border border-[color:var(--gray-a5)] [background-color:var(--color-panel-solid)]"
        >
          <Flex direction="column">
            <Text
              aria-label="Hovered portfolio value"
              size="2"
              weight="bold"
              className="atd-numeric"
            >
              {formatCurrency(hovered.value)}
            </Text>
            <Text
              aria-label="Hovered portfolio time"
              size="1"
              color="gray"
              className="atd-numeric"
            >
              {formatClockTime(hovered.time)}
            </Text>
          </Flex>
        </Box>
      ) : null}
    </Box>
  );
};
